class MainCart extends HTMLElement {
  constructor() {
    super();
    document.addEventListener(
      "mainCart:refresh",
      this.updateFromServer.bind(this)
    );
    this.elementId = this.getAttribute("id");
    this.cart = JSON.parse(
      this.querySelector("script[type='application/json']").textContent
    );
  }

  async updateFromServer() {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = await (await fetch(window.location.href)).text();
    document
      .getElementById(this.elementId)
      .replaceChildren(...tempDiv.querySelector(`#${this.elementId}`).children);
  }
}

if (!window.customElements.get("main-cart")) {
  window.customElements.define("main-cart", MainCart);
}

class MainCartDiscount extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector("button");
  }

  connectedCallback() {
    this.button.addEventListener("click", async (e) => {
      e.preventDefault();
      e.stopPropagation();
      const inputs = this.querySelectorAll("input[name='discount']");
      const values = Array.from(inputs).map((input) => input.value);
      await this.updateCart(values.join(","));
    });
  }

  async updateCart(discountCode) {
    const formData = new FormData();
    formData.append("discount", discountCode);

    const cart = await (
      await fetch("/cart/update.js", {
        method: "POST",
        body: formData,
      })
    ).json();

    const errorCard = cart.discount_codes.find(
      (code) => code.applicable === false
    );
    if (errorCard) {
      this.handleError(errorCard);
    } else {
      document.dispatchEvent(new CustomEvent("mainCart:refresh"));
    }
  }

  handleError(errorCard) {
    const errorMessage = this.querySelector("[data-error-message]");
    const baseMessage = errorMessage.dataset.errorMessage;
    errorMessage.removeAttribute("hidden");
    errorMessage.textContent = baseMessage.replace('""', `"${errorCard.code}"`);
  }
}

if (!window.customElements.get("main-cart-discount")) {
  window.customElements.define("main-cart-discount", MainCartDiscount);
}

class RemoveSet extends HTMLElement {
  constructor() {
    super();
    this._abortController = new AbortController();
  }

  connectedCallback() {
    const setId = this.getAttribute("remove-id");
    if (!setId) {
      console.error("RemoveSet: remove-id attribute is required");
      return;
    }

    // Set accessibility attributes
    this.setAttribute("role", "button");
    this.setAttribute("tabindex", "0");

    // Add keyboard support
    this.addEventListener("click", this._onRemoveSetClicked.bind(this), {
      signal: this._abortController.signal,
    });

    this.addEventListener("keydown", this._onKeyDown.bind(this), {
      signal: this._abortController.signal,
    });
  }

  _onKeyDown(event) {
    // Trigger on Enter or Space
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      this._onRemoveSetClicked(event);
    }
  }

  disconnectedCallback() {
    this._abortController.abort();
  }

  async _onRemoveSetClicked(event) {
    event.preventDefault();
    event.stopPropagation();

    const setId = this.getAttribute("remove-id");

    // Find all line items with the same set-id
    const lineItemElements = document.querySelectorAll(
      `[data-set-id="${setId}"]`
    );

    if (lineItemElements.length === 0) {
      console.warn(`RemoveSet: No line items found for set-id "${setId}"`);
      return;
    }

    // Collect line item keys
    const updates = {};
    lineItemElements.forEach((element) => {
      const key = element.getAttribute("data-key");
      if (key) {
        updates[key] = 0;
      }
    });

    if (Object.keys(updates).length === 0) {
      console.warn(
        `RemoveSet: No valid line item keys found for set-id "${setId}"`
      );
      return;
    }

    // Dispatch will-change event for each line item
    lineItemElements.forEach((element) => {
      const lineItem = element.closest("line-item");
      lineItem?.dispatchEvent(
        new CustomEvent("line-item:will-change", {
          bubbles: true,
          detail: { targetQuantity: 0 },
        })
      );
    });

    // Collect bundled sections for cart update
    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          updates,
          sections: sectionsToBundle,
        }),
      });

      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.statusText}`);
      }

      const cartContent = await response.json();

      // Clean up localStorage (screenshot)
      localStorage.removeItem(setId);

      // Dispatch cart:change event following theme pattern
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", {
          bubbles: true,
          detail: {
            baseEvent: "remove-set",
            cart: cartContent,
          },
        })
      );

      // On cart page, reload instead of using drawer
      if (window.themeVariables?.settings?.pageType === "cart") {
        window.location.reload();
      }
    } catch (error) {
      console.error("RemoveSet: Failed to remove set", error);

      // Dispatch error event
      lineItemElements.forEach((element) => {
        const lineItem = element.closest("line-item");
        lineItem?.dispatchEvent(
          new CustomEvent("line-item:error", { bubbles: true })
        );
      });
    }
  }
}

if (!window.customElements.get("remove-set")) {
  window.customElements.define("remove-set", RemoveSet);
}

// Custom Element for Configuration Screenshots (same as cart-drawer.js)
class ConfigScreenshot extends HTMLElement {
  constructor() {
    super();
    this._timeouts = [];
  }

  connectedCallback() {
    // Load immediately
    this._loadScreenshot();

    // Also use staggered timeouts to catch cart drawer's delayed innerHTML update
    this._timeouts.push(setTimeout(() => this._loadScreenshot(), 100));
    this._timeouts.push(setTimeout(() => this._loadScreenshot(), 500));
    this._timeouts.push(setTimeout(() => this._loadScreenshot(), 1300));
  }

  disconnectedCallback() {
    // Clean up timeouts
    this._timeouts.forEach((t) => clearTimeout(t));
    this._timeouts = [];
  }

  _loadScreenshot() {
    const setId = this.getAttribute("data-config-id");
    if (!setId) return;

    const img = this.querySelector("img");
    if (!img) return;

    // Skip if already loaded with valid data URL
    if (img.src && img.src.startsWith("data:")) return;

    const screenshot = localStorage.getItem(setId);
    if (screenshot) {
      img.src = screenshot;
      img.style.opacity = "1";
    }
  }
}

if (!window.customElements.get("config-screenshot")) {
  window.customElements.define("config-screenshot", ConfigScreenshot);
}
